// #RU_AUTO_V1 (2026-10-13) — the RU sheet's "pull from DOPE" logic, kept free of React so it can be tested on its own.
// Bill: rigs in the workbook's own sections (10x15–24x40 · 32AT · 40AT & 40x55 · AT120 & 100x140 · 220/330/Auger),
// sold rigs kept but collapsed with their sale date, a weather-day count per rig (after Days, before Note) off the
// daily reports, and the month built from weeks assigned by where MOST of the week's days fall.
import { unitKey } from './unitValid';
import { oemIndex, oemForUnit, oemWeekHours } from './oemFeed';

export const SUP_ALIAS = { 'RICHARD H': 'RICHARD', 'J PADILA': 'JUAN P', 'J PADILLA': 'JUAN P', 'J PADILLO': 'JUAN P', 'CODY G': 'CODY',
  'TY C': 'TY', 'NOE': 'NOEL S', 'EDWARD': 'EDWARD G' };
export const normSup = (s) => { const t = String(s || '').toUpperCase().replace(/\./g, ' ').replace(/\s+/g, ' ').trim(); return SUP_ALIAS[t] || t; };
export const addDays = (iso, n) => { const d = new Date(iso + 'T12:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };

// A Mon–Sun week belongs to the month holding 4+ of its 7 days = the month its Thursday is in.
export const monthOfWeek = (w) => addDays(w.weekStart, 3).slice(0, 7);
export const quarterOfWeek = (w) => `Q${Math.floor((+monthOfWeek(w).slice(5, 7) - 1) / 3) + 1}`;

// The workbook's rig sections, in sheet order. Colors are a validated categorical order (dark surface).
export const SECTIONS = [
  { key: 's1', label: '10x15 · 23x30 · 24x40', re: /^(10X15|23X30|24X40)/, color: '#3987e5' },
  { key: 's2', label: '32AT', re: /32AT/, color: '#d95926' },
  { key: 's3', label: '40AT · 40x55', re: /40AT|40X55/, color: '#199e70' },
  { key: 's4', label: 'AT120 · 100x140', re: /AT120|120AT|100X140/, color: '#c98500' },
  { key: 's5', label: '220 · 330 · Auger', re: /220|330|AUGER/, color: '#d55181' },
];
export const OTHER_SECTION = { key: 'sx', label: 'Other', re: /.^/, color: '#94A3B8' };
export const secOf = (rig) => { const s = String(rig || '').toUpperCase().replace(/\s+/g, ''); return (SECTIONS.find(x => x.re.test(s)) || OTHER_SECTION).key; };
export const secInfo = (key) => SECTIONS.find(s => s.key === key) || OTHER_SECTION;

// ---------- weather off the daily reports ----------
const WX_STAGE = /^(rain|snow|ice|weather|freeze|wind|heat)$/i;
const WX_TEXT = /\brain(ed)?[\s-]?(out|delay)|\brainout|\bweather\b|\bsnow(ed)?[\s-]?out|\biced?[\s-]?out|\btoo wet\b/i;
export const isWx = (e) => WX_STAGE.test(String(e.stage || '').trim()) || WX_TEXT.test(String(e.comment || ''));
const NOT_A_DAY = /^(holiday)$/i;

// "Ruben Mendez" → RM. Only a real two-part name counts ("Auger" on a job is a bucket, not a PM).
// Job number in one spelling: "CP-E4523906", "E4523906-CP" and "E4523906" are the same job.
export const ducKey = (x) => String(x || '').toUpperCase().replace(/^CP-|-CP$/g, '').replace(/[^A-Z0-9]/g, '');
// Rig model in one spelling: "AT 120 " = "AT120", "23x30DR" = "23X30".
export const rigNorm = (x) => String(x || '').toUpperCase().replace(/\s+/g, '').replace(/DR\d*$/, '');
export const pmInitials = (name) => { const w = String(name || '').trim().split(/\s+/).filter(Boolean); return w.length >= 2 ? (w[0][0] + w[w.length - 1][0]).toUpperCase() : ''; };
export const jobNote = (job, k) => {
  const duc = job.displayDuc || job.duc || (/^custom-/.test(k) ? '' : k);
  const where = [String(job.desc || job.customer || '').trim(), String(job.city || '').trim()].filter(Boolean).join(' ');
  return [duc, where].filter(Boolean).join('-').slice(0, 90);
};

// daily-logs { jobKey: { entries:[{date,sup,stage,comment,rig}] } }  →  { SUP: { date: [{stage,comment,note,rig}] } }
export const dailyIndex = (logs) => {
  const ix = {};
  Object.entries(logs || {}).forEach(([k, job]) => {
    if (!job || !Array.isArray(job.entries)) return;
    const note = jobNote(job, k);
    const pm = pmInitials(job.pm);
    const jk = ducKey(job.displayDuc || job.duc || (/^custom-/.test(k) ? '' : k));
    job.entries.forEach(e => {
      const s = normSup(e.sup); if (!s || s === 'OTHER' || !e.date) return;
      const d = ix[s] || (ix[s] = {}); (d[e.date] || (d[e.date] = [])).push({ stage: e.stage || '', comment: e.comment || '', note, pm, job: jk, rig: e.rig || '' });
    });
  });
  return ix;
};

// One supervisor's week off the daily reports.
export const supWeek = (ix, sup, from, to) => {
  const d = (ix && ix[normSup(sup)]) || {};
  const dates = Object.keys(d).filter(x => x >= from && x <= to).sort();
  const work = dates.filter(x => d[x].some(e => !NOT_A_DAY.test(String(e.stage).trim())));
  const wx = dates.filter(x => d[x].some(isWx)).map(x => ({ date: x, what: d[x].filter(isWx).map(e => (e.stage ? e.stage + ': ' : '') + String(e.comment || '').trim()).join(' · ').slice(0, 120) }));
  const notes = {}, pms = {}; dates.forEach(x => d[x].forEach(e => { if (e.note) notes[e.note] = (notes[e.note] || 0) + 1; if (e.pm) pms[e.pm] = (pms[e.pm] || 0) + 1; }));
  const pm = Object.entries(pms).sort((p, q) => q[1] - p[1]).map(([x]) => x)[0] || '';
  const note = Object.entries(notes).sort((a, b) => b[1] - a[1]).map(([n]) => n)[0] || '';
  // every job and every rig model he logged this week, by days (Bill: two jobs / two rigs in a week happens)
  const jd = {}, rd = {};
  dates.forEach(x => { new Set(d[x].map(e => e.note).filter(Boolean)).forEach(nt => { jd[nt] = (jd[nt] || 0) + 1; });
    new Set(d[x].map(e => rigNorm(e.rig)).filter(rg => rg && !/HYDROVAC|^PH$|N\/A/.test(rg))).forEach(rg => { rd[rg] = (rd[rg] || 0) + 1; }); });
  const jobs = Object.entries(jd).sort((p, q) => q[1] - p[1]).map(([nt, days]) => ({ note: nt, days }));
  const rigs = Object.entries(rd).sort((p, q) => q[1] - p[1]).map(([rig, days]) => ({ rig, days }));
  return { logged: dates.length > 0, days: work.length, wx: wx.length, wxList: wx, note, pm, jobs, rigs };
};
// Which rig a supervisor ran on a given job, off all his daily reports (most-logged rig for that job).
export const jobRig = (ix, sup, job) => {
  const d = (ix && ix[normSup(sup)]) || {}; const k = ducKey(job); if (!k) return '';
  const c = {}; Object.values(d).forEach(list => list.forEach(e => { if (e.job === k && e.rig) { const rg = rigNorm(e.rig); c[rg] = (c[rg] || 0) + 1; } }));
  return Object.entries(c).sort((p, q) => q[1] - p[1]).map(([rg]) => rg)[0] || '';
};
export const supsLogged = (ix, from, to) => Object.keys(ix || {}).filter(s => Object.keys(ix[s]).some(x => x >= from && x <= to));

// ---------- fleet master (sold / retired rigs, VINs) ----------
export const masterUnits = (blob) => {
  const out = {}; if (!blob || !Array.isArray(blob.cols)) return out;
  const put = (rows, retired) => (rows || []).forEach(a => {
    if (!Array.isArray(a)) return; const r = {}; blob.cols.forEach((k, i) => { r[k] = a[i]; });
    if (r.unit) out[unitKey(r.unit)] = { unit: r.unit, vin: r.vin || '', cat: r.cat || '', saleDate: r.saleDate || '', retired };
  });
  put(blob.active, false); put(blob.retired, true);
  return out;
};
export const soldInfo = (master, unit) => { const m = master && master[unitKey(unit)]; return m && m.retired ? { saleDate: m.saleDate || '' } : null; };

// ---------- hour meters: Bob's weekly inventory reading first, then the factory feed ----------
export const meterAt = (weekly, oemIx, master, unit, mondayISO) => {
  const w = weekly && weekly.weeks && weekly.weeks[mondayISO]; const r = w && w[unitKey(unit)];
  if (r && r.reading !== '' && r.reading != null && isFinite(+r.reading)) return { h: +r.reading, src: r.source === 'oem' ? 'factory feed' : r.source === 'geotab' ? 'Geotab' : 'weekly inventory' };
  const m = master && master[unitKey(unit)]; const dev = m && m.vin ? oemForUnit(oemIx, m.vin) : null; const h = oemWeekHours(dev, mondayISO);
  return h != null ? { h, src: 'factory feed' } : null;
};
export { oemIndex };

const r1 = (x) => Math.round(x * 10) / 10;

// Fill an OPEN week from DOPE. Only blanks are filled (a typed value is never overwritten) — weather is always refreshed.
export const autoFillWeek = (W, ctx) => {
  const { ix, weekly, oemIx, master } = ctx;
  const nextMon = addDays(W.weekEnd, 1);
  const rep = { days: 0, wx: 0, notes: 0, meters: 0, pms: 0, noMeter: [], neg: [], offRoster: [] };
  const rows = (W.rows || []).map(r => {
    const n = { ...r };
    if (soldInfo(master, r.unit)) return n;
    if (!r.sup) {   // #RU_EMPTYROW_V1 — a row with no SUPV keeps only the rig (meters) and a typed non-job note
      ['days', 'wx', 'wxList', 'lf', 'rev', 'bores', 'crewHr', 'crewOt', 'supHr', 'crewNames', 'jobs', 'rigsUsed', 'revSplit'].forEach(f => { delete n[f]; });
      if (/^(CP-)?[EFGPSW]\d{6}/i.test(String(n.note || ''))) delete n.note;
    }
    if (r.sup) {
      const s = supWeek(ix, r.sup, W.weekStart, W.weekEnd);
      if (s.logged) {
        if (n.days == null || n.days === '') { n.days = s.days; rep.days++; }
        if (!n.note) { n.note = s.note; if (s.note) rep.notes++; }
        if (s.pm && s.pm !== n.pm) { n.pm = s.pm; rep.pms = (rep.pms || 0) + 1; }   // PM follows the job the supervisor logged most this week
      }
      n.wx = s.wx; n.wxList = s.wxList; if (s.wx) rep.wx += s.wx;
      n.jobs = s.jobs.length > 1 ? s.jobs : undefined; n.rigsUsed = s.rigs.length > 1 ? s.rigs : undefined;
      if (n.jobs === undefined) delete n.jobs; if (n.rigsUsed === undefined) delete n.rigsUsed;
    }
    // Start = the reading taken the Monday the week opens (same source as End, so the week is Monday-to-Monday).
    // A Start carried from last week's End is only a placeholder; a typed Start is never replaced.
    if (n.start == null || n.startCarried) { const a = meterAt(weekly, oemIx, master, r.unit, W.weekStart); if (a) { n.start = a.h; n.startSrc = a.src; delete n.startCarried; } }
    if (n.end == null) { const b = meterAt(weekly, oemIx, master, r.unit, nextMon); if (b) { n.end = b.h; n.endSrc = b.src; rep.meters++; } else if (r.sup) rep.noMeter.push(r.unit); }
    if (n.start != null && n.end != null) { n.hrs = r1(n.end - n.start); if (n.hrs < 0) { rep.neg.push(`${r.unit} (${n.start}→${n.end})`); n.hrs = 0; n.meterFlag = true; } }
    return n;
  });
  const onRoster = new Set(rows.filter(r => r.sup).map(r => normSup(r.sup)));
  rep.offRoster = supsLogged(ix, W.weekStart, W.weekEnd).filter(s => !onRoster.has(s));
  return { rows, rep };
};

// #RU_AWARDS_V1 — RU winner groups, as the workbook's quarter ranking block lays them out: the five rig groups with
// the Auger split out on its own (Bill: "there are 5 groups + Auger").
export const AWARD_GROUPS = [
  { key: 'g1', label: '10x15 · 23x30 · 24x40', color: '#3987e5', test: (rig) => secOf(rig) === 's1' },
  { key: 'g2', label: '32AT', color: '#d95926', test: (rig) => secOf(rig) === 's2' },
  { key: 'g3', label: '40AT · 40x55', color: '#199e70', test: (rig) => secOf(rig) === 's3' },
  { key: 'g4', label: 'AT120 · 100x140', color: '#c98500', test: (rig) => secOf(rig) === 's4' },
  { key: 'g5', label: '220 · 330', color: '#d55181', test: (rig) => secOf(rig) === 's5' && !/AUGER/i.test(String(rig || '')) },
  { key: 'g6', label: 'Auger', color: '#9085e9', noAward: true, test: (rig) => /AUGER/i.test(String(rig || '')) },   // shown, not in the RU award
];
export const groupOf = (rig) => (AWARD_GROUPS.find(g => g.test(rig)) || { key: 'gx' }).key;
export const groupLabel = (k) => (AWARD_GROUPS.find(g => g.key === k) || { label: 'Other' }).label;

// Supervisor names for the SUPV picker, in the same spelling the matching uses (daily reports + roster + revenue).
export const supChoices = (ix, rows, rev, fromISO) => {
  const out = new Set();
  Object.keys(ix || {}).forEach(sp => { if (Object.keys(ix[sp]).some(d => d >= fromISO)) out.add(sp); });
  (rows || []).forEach(r => { if (r.sup) out.add(normSup(r.sup)); });
  (rev || []).forEach(r => { const sp = normSup(r.sup); if (sp && sp !== 'OTHER') out.add(sp); });
  return [...out].filter(x => x && x !== 'OTHER').sort();
};
export const inAward = (k) => !(AWARD_GROUPS.find(g => g.key === k) || {}).noAward;
